import { useTranslation } from "react-i18next";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import type React from "react";
import type { UserProfile } from "@/features/auth/contexts/auth/context";
import { useActiveTeam } from "@/services/teams/useActiveTeam";
import { isFeatureEnabled } from "@/services/feature-flags";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "@/components/ui/tooltip";
import { Logo } from "./Logo";

/**
 * WorkspaceSwitcher — the sidebar header. Reuses the existing active-team store
 * (`useActiveTeam`) so selecting a workspace sets the `X-Team-Id` the API layer
 * attaches; "Personal" clears it. This is the shell's replacement for the old
 * top-bar TeamSwitcher — a single source of team switching.
 *
 * With no teams, a switcher would be noise, so it renders static branding
 * instead (unrelated to sidebar `collapsed` below — that flag drives the
 * icon-only rail state, orthogonal to whether teams exist at all).
 *
 * When `collapsed`, both branches drop to just the Logo mark for sighted
 * users and add a right-side Tooltip carrying the hidden text back for
 * mouse hover. The static-branding branch additionally keeps its label
 * text in the DOM as `sr-only` (rather than removing it) so screen readers
 * still get it regardless of focus, since the wrapping element is a plain,
 * non-interactive `<div>`; the switcher branch is a real, focusable button,
 * so it gets an explicit `aria-label` instead.
 */
export function WorkspaceSwitcher({
  profile,
  collapsed = false,
}: {
  profile: UserProfile | null;
  collapsed?: boolean;
}): React.ReactElement {
  const { t } = useTranslation("common");
  const { teams, activeTeamId, activeTeam, setActiveTeam } =
    useActiveTeam(profile);

  // The auto-created personal team duplicates the "personal" (no-team) scope,
  // so it is never a separate switcher entry — the "Osobiste"/"Personal" option
  // already represents it. Only real collaborative teams get listed.
  const selectableTeams = teams.filter((team) => !team.isPersonal);

  const mainLabel = activeTeam?.name ?? "Vardic";
  const subLabel = activeTeam?.role
    ? t(`shell.roles.${activeTeam.role}` as "shell.roles.owner" | "shell.roles.admin" | "shell.roles.member")
    : t("shell.personalWorkspace");

  // Teams feature is gated off by default (pirx-qy6v4): with teams hidden the
  // switcher is never offered, so the header collapses to static branding.
  if (!isFeatureEnabled("TEAMS") || selectableTeams.length === 0) {
    const brand = (
      <div
        className={cn(
          "flex items-center gap-2.5 rounded-[var(--r-md)] py-2",
          collapsed ? "justify-center px-0" : "px-2.5",
        )}
        data-test="workspace-brand"
      >
        <Logo size={32} />
        <div className={cn("min-w-0 flex-1", collapsed && "sr-only")}>
          {/* eslint-disable-next-line i18next/no-literal-string -- brand wordmark, not translatable */}
          <div className="truncate font-bold text-sm text-white tracking-[-0.01em]">
            Vardic
          </div>
          <div className="truncate font-[var(--mono)] text-[11px] text-[var(--nav-ink-faint)]">
            {t("shell.personalWorkspace")}
          </div>
        </div>
      </div>
    );
    return collapsed ? (
      <Tooltip>
        <TooltipTrigger render={brand} />
        <TooltipPopup side="right">{t("shell.personalWorkspace")}</TooltipPopup>
      </Tooltip>
    ) : (
      brand
    );
  }

  const trigger = (
    <DropdownMenuTrigger
      aria-label={collapsed ? mainLabel : undefined}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-[var(--r-md)] border border-transparent py-2 text-left outline-none transition-colors hover:bg-[var(--nav-bg-2)] data-[popup-open]:border-[var(--nav-line)] data-[popup-open]:bg-[var(--nav-bg-2)]",
        collapsed ? "justify-center px-0" : "px-2.5",
      )}
      data-test="workspace-switcher"
      render={<button type="button" />}
    >
      <Logo size={32} />
      {!collapsed && (
        <>
          <div className="min-w-0 flex-1">
            <div className="truncate font-bold text-sm text-white tracking-[-0.01em]">
              {mainLabel}
            </div>
            <div className="truncate text-[11px] text-[var(--nav-ink-faint)]">
              {subLabel}
            </div>
          </div>
          <ChevronsUpDownIcon className="size-4 shrink-0 text-[var(--nav-ink-2)]" />
        </>
      )}
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger render={trigger} />
          <TooltipPopup side="right">{mainLabel}</TooltipPopup>
        </Tooltip>
      ) : (
        trigger
      )}
      <DropdownMenuContent className="w-[232px]">
        <DropdownMenuLabel>{t("shell.switchWorkspace")}</DropdownMenuLabel>
        <DropdownMenuItem
          data-test="workspace-option-personal"
          onClick={() => setActiveTeam(null)}
        >
          <span className="flex-1 truncate">
            {t("shell.personalWorkspace")}
          </span>
          {activeTeamId === null ? (
            <CheckIcon className="text-[var(--brand)]" />
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {selectableTeams.map((team) => (
          <DropdownMenuItem
            key={team.id}
            data-test={`workspace-option-${team.id}`}
            onClick={() => setActiveTeam(team.id)}
          >
            <span className="flex-1 truncate">{team.name}</span>
            {activeTeamId === team.id ? (
              <CheckIcon className="text-[var(--brand)]" />
            ) : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
